import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ChevronDown, ChevronLeft, ChevronRight, Cloud, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cashFlowQueryOptions } from '@/features/reports/api'
import { SignedBars } from '@/features/reports/panel-bars'
import { janelaAno, painelSeries } from '@/features/reports/panel-series'
import { cn } from '@/shared/lib/cn'
import { fromCents, type Cents } from '@/shared/money/money'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { Skeleton } from '@/shared/ui/skeleton'
import { PageHeader } from '@/widgets/app-shell/page-header'

export const Route = createFileRoute('/_authed/relatorios/painel-de-acompanhamento')({
  component: Page,
})

/** '—' para zero/ausente — menos ruído que R$ 0,00 repetido 13 vezes por linha. */
const DASH = '—'

type RowKind = 'data' | 'section' | 'sub' | 'subtotal' | 'percent' | 'result'
type Tone = 'in' | 'out' | 'net' | 'muted'

/** Chaves das seções do print — literais para o `t()` tipado aceitar a chave montada. */
type SectionKey =
  | 'operatingRevenue'
  | 'salesRevenue'
  | 'operatingCosts'
  | 'costOfGoods'
  | 'costOfServices'
  | 'contributionMargin'
  | 'contributionPercent'
  | 'operatingExpenses'
  | 'payroll'
  | 'financialExpenses'
  | 'operatingResult'
  | 'fixedAssets'
  | 'investing'
  | 'financing'
  | 'cashVariation'

/**
 * MOLDURA do DRE/DFC do print. O back-end ainda NÃO fornece quebra por
 * categoria/atividade — então estas linhas vêm SEM número (só rótulo + "—"). O
 * dado real (Entradas/Saídas/Resultado por mês) vem do cash-flow, no bloco de
 * cima. Ver `panel-series` e a nota ao pé da tabela.
 */
const STRUCTURE: readonly { readonly key: SectionKey; readonly kind: RowKind }[] = [
  { key: 'operatingRevenue', kind: 'section' },
  { key: 'salesRevenue', kind: 'sub' },
  { key: 'operatingCosts', kind: 'section' },
  { key: 'costOfGoods', kind: 'sub' },
  { key: 'costOfServices', kind: 'sub' },
  { key: 'contributionMargin', kind: 'subtotal' },
  { key: 'contributionPercent', kind: 'percent' },
  { key: 'operatingExpenses', kind: 'section' },
  { key: 'payroll', kind: 'sub' },
  { key: 'financialExpenses', kind: 'sub' },
  { key: 'operatingResult', kind: 'result' },
  { key: 'fixedAssets', kind: 'sub' },
  { key: 'investing', kind: 'result' },
  { key: 'financing', kind: 'result' },
  { key: 'cashVariation', kind: 'result' },
]

function Page() {
  const { t, i18n } = useTranslation(['reports', 'nav', 'common'])
  const lang = i18n.language
  const [ano, setAno] = useState(() => new Date().getFullYear())

  const janela = useMemo(() => janelaAno(ano), [ano])
  const q = useQuery(
    cashFlowQueryOptions({
      from: janela.from,
      to: janela.to,
      group_by: 'month',
      // Regime de caixa realizado; projeção não entra nesta visão.
      include_projected: false,
      // Contas integradas (Hatch) entram na tabela e nos gráficos.
      include_bank: true,
    }),
  )
  const serie = useMemo(() => (q.data ? painelSeries(q.data) : null), [q.data])

  const decimalFmt = useMemo(
    () => new Intl.NumberFormat(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    [lang],
  )
  const formatCents = (c: number) => decimalFmt.format(fromCents(c as Cents))

  const mesFmt = useMemo(() => new Intl.DateTimeFormat(lang, { month: 'short' }), [lang])
  const mesCurto = (period: string) => {
    const [y, m] = period.split('-').map(Number)
    return mesFmt.format(new Date(y ?? 1970, (m ?? 1) - 1, 1))
  }

  const compacto = (c: number) =>
    new Intl.NumberFormat(lang, {
      style: 'currency',
      currency: 'BRL',
      notation: 'compact',
      maximumFractionDigits: 2,
    }).format(c / 100)

  const fmtCell = (v: number | null, tone: Tone): { text: string; cls: string } => {
    if (v === null || v === 0) return { text: DASH, cls: 'text-ink-subtle' }
    const cls =
      tone === 'in'
        ? 'text-income'
        : tone === 'out'
          ? 'text-expense'
          : tone === 'net'
            ? v < 0
              ? 'text-expense'
              : 'text-income'
            : 'text-ink'
    return { text: formatCents(v), cls }
  }

  const monthsLen = serie?.months.length ?? 0

  const renderRow = (
    key: string,
    label: string,
    kind: RowKind,
    tone: Tone,
    cells: readonly (number | null)[],
    total: number | null,
  ) => {
    const rowBg = kind === 'result' ? 'bg-action-bg' : 'bg-surface'
    const weight =
      kind === 'result'
        ? 'font-semibold'
        : kind === 'section' || kind === 'subtotal'
          ? 'font-medium'
          : 'font-normal'
    const indent = kind === 'sub' ? 'pl-8' : 'pl-3'
    const labelColor = kind === 'sub' || kind === 'percent' ? 'text-ink-muted' : 'text-ink'
    const totalCell = fmtCell(total, tone)

    return (
      <tr key={key} className={rowBg}>
        <th
          scope="row"
          className={cn(
            'border-line sticky left-0 z-10 border-b py-1.5 pr-6 text-left text-sm whitespace-nowrap',
            rowBg,
            indent,
            weight,
            labelColor,
          )}
        >
          {label}
        </th>
        {cells.map((v, i) => {
          const c = fmtCell(v, tone)
          return (
            <td
              key={`${key}-${String(i)}`}
              className={cn('border-line tnum border-b px-3 py-1.5 text-right text-sm', c.cls)}
            >
              {c.text}
            </td>
          )
        })}
        <td
          className={cn(
            'border-line tnum border-b border-l px-3 py-1.5 text-right text-sm font-medium',
            totalCell.cls,
          )}
        >
          {totalCell.text}
        </td>
      </tr>
    )
  }

  const groupRow = (key: string, label: string) => (
    <tr key={key} className="bg-surface-2">
      <td
        colSpan={monthsLen + 2}
        className="border-line text-ink-muted text-2xs border-b px-3 py-2 font-semibold tracking-wide uppercase"
      >
        {label}
      </td>
    </tr>
  )

  const emptyCells = Array.from({ length: monthsLen }, () => null)

  const chartRevenue = serie?.months.map((m) => ({ period: m.period, value: m.inflows })) ?? []
  const chartResult = serie?.months.map((m) => ({ period: m.period, value: m.net })) ?? []

  const planButton = (
    <div className="flex items-center gap-2">
      <Button variant="secondary" size="sm" disabled title={t('reports:panel.budgetSoon')}>
        <Cloud aria-hidden />
        {t('reports:panel.planBudget')}
      </Button>
      <Badge tone="soon">{t('common:comingSoon.badge')}</Badge>
    </div>
  )

  return (
    <>
      <PageHeader title={t('nav:reports.dashboard')} />

      <div className="flex flex-col gap-4 p-6">
        {/* --- Controles do topo --- */}
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
            <span aria-hidden />
            <div className="flex items-center justify-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('reports:panel.year.prev')}
                onClick={() => {
                  setAno((y) => y - 1)
                }}
              >
                <ChevronLeft aria-hidden />
              </Button>
              <span className="tnum text-ink min-w-16 text-center text-base font-semibold">
                {ano}
              </span>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('reports:panel.year.next')}
                onClick={() => {
                  setAno((y) => y + 1)
                }}
              >
                <ChevronRight aria-hidden />
              </Button>
            </div>
            <div className="flex justify-end">{planButton}</div>
          </div>

          <div className="flex flex-wrap gap-x-10 gap-y-3">
            <fieldset className="flex flex-col gap-1.5">
              <legend className="text-ink-muted mb-1 text-xs font-medium">
                {t('reports:panel.viewBy.label')}
              </legend>
              <div className="flex items-center gap-4">
                <label className="text-ink flex items-center gap-1.5 text-sm">
                  <input type="radio" name="viewBy" defaultChecked className="accent-action" />
                  {t('reports:panel.viewBy.cash')}
                </label>
                <label
                  className="text-ink-subtle flex items-center gap-1.5 text-sm"
                  title={t('reports:panel.accrualSoon')}
                >
                  <input type="radio" name="viewBy" disabled />
                  {t('reports:panel.viewBy.accrual')}
                  <Badge tone="soon">{t('common:comingSoon.badge')}</Badge>
                </label>
              </div>
            </fieldset>

            <fieldset className="flex flex-col gap-1.5">
              <legend className="text-ink-muted mb-1 text-xs font-medium">
                {t('reports:panel.vision.label')}
              </legend>
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-ink flex items-center gap-1.5 text-sm">
                  <input type="radio" name="vision" defaultChecked className="accent-action" />
                  {t('reports:panel.vision.actual')}
                </label>
                <label
                  className="text-ink-subtle flex items-center gap-1.5 text-sm"
                  title={t('reports:panel.visionSoon')}
                >
                  <input type="radio" name="vision" disabled />
                  {t('reports:panel.vision.actualVsScheduled')}
                  <Badge tone="soon">{t('common:comingSoon.badge')}</Badge>
                </label>
                <label
                  className="text-ink-subtle flex items-center gap-1.5 text-sm"
                  title={t('reports:panel.visionSoon')}
                >
                  <input type="radio" name="vision" disabled />
                  {t('reports:panel.vision.actualVsBudget')}
                  <Badge tone="soon">{t('common:comingSoon.badge')}</Badge>
                </label>
              </div>
            </fieldset>
          </div>
        </div>

        {/* --- Tabela pivô (DRE/DFC) --- */}
        <Card className="overflow-hidden">
          {q.isLoading ? (
            <div className="p-4">
              <Skeleton className="h-96 w-full" />
            </div>
          ) : q.isError || !serie ? (
            <p className="text-ink-subtle p-6 text-sm">{t('reports:loadError')}</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table
                  className="w-full min-w-[960px] border-collapse"
                  aria-label={t('reports:panel.table.aria')}
                >
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="border-line bg-surface-2 text-ink-muted text-2xs sticky left-0 z-10 border-b px-3 py-2 text-left font-semibold uppercase"
                      >
                        {t('reports:panel.table.resultHeader')}
                      </th>
                      {serie.months.map((m) => (
                        <th
                          key={m.period}
                          scope="col"
                          className="border-line bg-surface-2 text-ink-muted text-2xs border-b px-3 py-2 text-right font-semibold"
                        >
                          {mesCurto(m.period)}
                        </th>
                      ))}
                      <th
                        scope="col"
                        className="border-line bg-surface-2 text-ink-muted text-2xs border-b border-l px-3 py-2 text-right font-semibold"
                      >
                        {String(ano)}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Bloco REAL (o que o cash-flow entrega de verdade). */}
                    {groupRow('grp-real', t('reports:panel.table.realizedGroup'))}
                    {renderRow(
                      'inflows',
                      t('reports:panel.table.inflows'),
                      'data',
                      'in',
                      serie.months.map((m) => m.inflows),
                      serie.totals.inflows,
                    )}
                    {renderRow(
                      'outflows',
                      t('reports:panel.table.outflows'),
                      'data',
                      'out',
                      serie.months.map((m) => m.outflows),
                      serie.totals.outflows,
                    )}
                    {renderRow(
                      'net',
                      t('reports:panel.table.net'),
                      'result',
                      'net',
                      serie.months.map((m) => m.net),
                      serie.totals.net,
                    )}

                    {/* MOLDURA por categoria/atividade — sem dado no back-end. */}
                    {groupRow('grp-structure', t('reports:panel.table.structureGroup'))}
                    {STRUCTURE.map((row) =>
                      renderRow(
                        row.key,
                        t(`reports:panel.table.sections.${row.key}`),
                        row.kind,
                        'muted',
                        emptyCells,
                        null,
                      ),
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-ink-muted border-line border-t px-4 py-3 text-xs">
                {t('reports:panel.structureNote')}
              </p>
            </>
          )}
        </Card>

        {/* --- Balanço resumido (sem back-end) --- */}
        <Card>
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <h2 className="text-ink text-base font-semibold">
                {t('reports:panel.balance.title')}
              </h2>
              <Badge tone="soon">{t('common:comingSoon.badge')}</Badge>
            </div>
            <ChevronDown className="text-ink-muted size-4" aria-hidden />
          </div>
          <p className="text-ink-muted border-line border-t px-4 py-3 text-sm">
            {t('reports:panel.balance.soon')}
          </p>
        </Card>

        {/* --- Indicadores chave (sem back-end) --- */}
        <Card>
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <h2 className="text-ink text-base font-semibold">
                {t('reports:panel.indicators.title')}
              </h2>
              <Badge tone="soon">{t('common:comingSoon.badge')}</Badge>
            </div>
            <Button variant="link" size="sm" disabled title={t('reports:panel.indicators.soon')}>
              <Plus aria-hidden />
              {t('reports:panel.indicators.new')}
            </Button>
          </div>
          <p className="text-ink-muted border-line border-t px-4 py-3 text-sm">
            {t('reports:panel.indicators.soon')}
          </p>
        </Card>

        {/* --- Gráficos (dado real do cash-flow) --- */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('reports:panel.charts.revenue')}</CardTitle>
          </CardHeader>
          <CardContent>
            {q.isLoading || !serie ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <SignedBars
                data={chartRevenue}
                formatValue={compacto}
                formatPeriod={mesCurto}
                ariaLabel={t('reports:panel.charts.revenue')}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('reports:panel.charts.operatingResult')}</CardTitle>
          </CardHeader>
          <CardContent>
            {q.isLoading || !serie ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <SignedBars
                data={chartResult}
                formatValue={compacto}
                formatPeriod={mesCurto}
                ariaLabel={t('reports:panel.charts.operatingResult')}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}
